import { CheckIcon, InfoIcon, LockIcon, PulseIcon, ShieldIcon } from '@/components/common/icons';
import { SCREEN } from '@/components/common/surfaces';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface LoginPageProps {
  onSignIn: () => void;
  /** Shown above the button when the last sign-in attempt failed. */
  error?: string;
}

const Point: FC<{ icon: ReactNode; children: ReactNode }> = ({ icon, children }) => (
  <div className="flex items-center gap-[11px] text-[13px] text-white/[.88]">
    {icon}
    {children}
  </div>
);

/**
 * The prototype's sign-in screen: the navy panel with the promise on the left, the SSO button on
 * the right. The button starts the Keycloak redirect; the app has no credentials of its own.
 */
export const LoginPage: FC<LoginPageProps> = ({ onSignIn, error }) => {
  const { t } = useTranslation();

  return (
    <div className={`${SCREEN} items-center justify-center px-7 pb-[72px] pt-9`}>
      <div className="grid w-full max-w-[1120px] animate-[fadein_.25s_ease] grid-cols-[minmax(0,1.12fr)_minmax(0,1fr)] overflow-hidden rounded bg-white shadow-[0_2px_12px_rgba(10,45,80,.07)] max-[920px]:grid-cols-1">
        <div className="relative flex min-h-[470px] flex-col overflow-hidden bg-[linear-gradient(145deg,#0e4677_0%,#0a3159_100%)] px-11 pb-11 pt-10 text-white max-[620px]:hidden">
          <div className="absolute -right-[150px] top-[70px] h-[430px] w-[430px] rounded-full border-[44px] border-[rgba(46,182,224,.10)]" />
          <div className="absolute -right-10 top-[180px] h-[300px] w-[300px] rounded-full border border-[rgba(46,182,224,.22)]" />
          <img
            src="/bvk-krokodyl.webp"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute bottom-0 right-5 w-[190px] max-[1180px]:min-[921px]:hidden drop-shadow-[0_8px_14px_rgba(0,0,0,.35)]"
          />
          <img
            src="/bvk-logo-white.png"
            alt={t('app.organisation')}
            className="relative block h-11 w-auto self-center"
          />
          <div className="min-h-9 flex-[0.8]" />
          <h1 className="relative m-0 mb-[18px] max-w-[330px] text-[33px] font-bold leading-[1.16] [text-wrap:pretty]">
            {t('login.headline')}
          </h1>
          <p className="relative m-0 max-w-[370px] text-sm leading-[1.6] text-white/[.78]">{t('login.lead')}</p>
          <div className="min-h-10 flex-1" />
          <div className="relative flex flex-col gap-[13px]">
            <Point icon={<ShieldIcon size={15} stroke="#2eb6e0" />}>{t('login.pointNetwork')}</Point>
            <Point icon={<PulseIcon size={15} stroke="#2eb6e0" />}>{t('login.pointFlow')}</Point>
            <Point icon={<CheckIcon size={15} stroke="#2eb6e0" strokeWidth={2.4} />}>{t('login.pointDecision')}</Point>
          </div>
        </div>
        <div className="flex flex-col justify-center px-12 py-14">
          <h2 className="m-0 mb-2 text-[19px] font-bold text-primary">{t('login.title')}</h2>
          <p className="m-0 mb-[22px] text-[13.5px] leading-[1.6] text-muted">{t('login.hint')}</p>
          {error ? (
            <p role="alert" className="m-0 mb-4 bg-danger-soft px-3.5 py-2.5 text-[12.5px] leading-[1.5] text-danger">
              {error}
            </p>
          ) : null}
          <button
            type="button"
            onClick={onSignIn}
            className="flex w-full cursor-pointer items-center gap-[9px] rounded border-none bg-primary px-4 py-[13px] text-sm font-semibold text-white hover:bg-primary-deep"
          >
            <LockIcon size={15} />
            {t('auth.signInWithSso')}
          </button>
          <div className="mt-4 flex gap-2">
            <InfoIcon size={14} stroke="#93a5b5" className="mt-0.5 flex-none" />
            <div className="text-[11.5px] leading-[1.55] text-subtle">{t('login.note')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
